import React from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, TrendingUp, Users, Calendar, Building } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface CaseStudyLayoutProps {
  caseStudy: {
    id: string;
    company: string;
    industry: string;
    service: string;
    duration: string;
    image: string;
    challenge: string;
    solution: string;
    results: {
      traffic: { before: string; after: string; };
      leads: { before: string; after: string; };
      conversion: { before: string; after: string; };
    };
    testimonial?: {
      quote: string;
      author: string;
      position: string;
    };
  };
}

const CaseStudyLayout: React.FC<CaseStudyLayoutProps> = ({ caseStudy }) => {
  const { currentLanguage } = useLanguage();

  React.useEffect(() => {
    // Add JSON-LD structured data
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": `${caseStudy.company} - ${caseStudy.service} Erfolgsgeschichte`,
      "description": `Wie ${caseStudy.company} mit ${caseStudy.service} messbare Erfolge erzielte`,
      "author": {
        "@type": "Organization",
        "name": "Future Level"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Future Level",
        "logo": {
          "@type": "ImageObject",
          "url": "https://futurelevel.ch/logo.png"
        }
      },
      "datePublished": new Date().toISOString(),
      "image": caseStudy.image,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": window.location.href
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [caseStudy]);

  const getGrowthPercentage = (before: string, after: string) => {
    const beforeNum = parseInt(before.replace(/[,\.]/g, ''));
    const afterNum = parseInt(after.replace(/[,\.]/g, ''));
    return Math.round(((afterNum - beforeNum) / beforeNum) * 100);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow pt-20">
        {/* Breadcrumbs */}
        <div className="container mx-auto px-4 py-4">
          <nav aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-sm text-muted-foreground">
              <li><a href={`/${currentLanguage}/`} className="hover:text-primary">Home</a></li>
              <li>›</li>
              <li><a href={`/${currentLanguage}/case-studies/`} className="hover:text-primary">Case Studies</a></li>
              <li>›</li>
              <li className="text-foreground">{caseStudy.company}</li>
            </ol>
          </nav>
        </div>

        {/* Hero Section */}
        <section className="py-12 bg-gradient-to-br from-background to-background/80">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge variant="outline" className="mb-4">
                  {caseStudy.industry}
                </Badge>
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  {caseStudy.company}
                </h1>
                <p className="text-xl text-muted-foreground mb-6">
                  {caseStudy.service} • {caseStudy.duration}
                </p>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar size={16} />
                    <span>{caseStudy.duration}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Building size={16} />
                    <span>{caseStudy.industry}</span>
                  </div>
                </div>
              </div>
              <div className="lg:order-first">
                <img 
                  src={caseStudy.image}
                  alt={caseStudy.company}
                  className="w-full h-80 object-cover rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-16 bg-background/50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Messbare Resultate</h2>
            <div className="grid md:grid-cols-3 gap-8">
              {/* Traffic */}
              <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 p-8 rounded-lg border border-green-500/20 text-center">
                <TrendingUp className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Organischer Traffic</h3>
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground">Vorher: {caseStudy.results.traffic.before}</div>
                  <div className="text-3xl font-bold text-green-500">{caseStudy.results.traffic.after}</div>
                  <div className="text-sm text-green-600">
                    +{getGrowthPercentage(caseStudy.results.traffic.before, caseStudy.results.traffic.after)}% Steigerung
                  </div>
                </div>
              </div>

              {/* Leads */}
              <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 p-8 rounded-lg border border-blue-500/20 text-center">
                <Users className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Monatliche Leads</h3>
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground">Vorher: {caseStudy.results.leads.before}</div>
                  <div className="text-3xl font-bold text-blue-500">{caseStudy.results.leads.after}</div>
                  <div className="text-sm text-blue-600">
                    +{getGrowthPercentage(caseStudy.results.leads.before, caseStudy.results.leads.after)}% Steigerung
                  </div>
                </div>
              </div>

              {/* Conversion Rate */}
              <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 p-8 rounded-lg border border-purple-500/20 text-center">
                <TrendingUp className="w-12 h-12 text-purple-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Conversion Rate</h3>
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground">Vorher: {caseStudy.results.conversion.before}</div>
                  <div className="text-3xl font-bold text-purple-500">{caseStudy.results.conversion.after}</div>
                  <div className="text-sm text-purple-600">
                    +{getGrowthPercentage(caseStudy.results.conversion.before.replace('%', ''), caseStudy.results.conversion.after.replace('%', ''))}% Steigerung
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Challenge & Solution */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold mb-6">Die Herausforderung</h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {caseStudy.challenge}
                </p>
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-6">Unsere Lösung</h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {caseStudy.solution}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial */}
        {caseStudy.testimonial && (
          <section className="py-16 bg-background/50">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <blockquote className="text-2xl font-medium leading-relaxed mb-8">
                  "{caseStudy.testimonial.quote}"
                </blockquote>
                <div>
                  <div className="font-semibold">{caseStudy.testimonial.author}</div>
                  <div className="text-muted-foreground">{caseStudy.testimonial.position}</div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">Ähnliche Erfolge für Ihr Unternehmen?</h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Lassen Sie uns gemeinsam eine Strategie entwickeln, die zu messbaren Resultaten führt.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <a href={`/${currentLanguage}/kontakt/`}>
                  Strategiegespräch buchen
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href={`/${currentLanguage}/case-studies/`}>
                  <ArrowLeft className="mr-2" size={20} />
                  Weitere Case Studies
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CaseStudyLayout;